const CACHE_NAME = 'promo-screenshots-cache';
const memoryCache = {};
const inFlightRequests = {};

/**
 * Gets a screenshot image URL (Object URL) from cache or network,
 * with request deduplication and local persistence.
 * 
 * @param {string} s3Key The S3 key of the screenshot (e.g. "path/to/image.png")
 * @param {string} apiUrl The API url to fetch the signed URL
 * @param {string} apiKey The API key for authorization
 * @returns {Promise<string>} A promise resolving to the object URL
 */
export const getCachedScreenshotUrl = async (s3Key, apiUrl, apiKey) => {
  // 1. Check memory cache (fastest)
  if (memoryCache[s3Key]) {
    return memoryCache[s3Key];
  }

  // 2. Check if there is already an in-flight request for this S3 key
  if (inFlightRequests[s3Key]) {
    return inFlightRequests[s3Key];
  }

  // Helper to fetch and cache
  const fetchAndCache = async () => {
    try {
      // Check Cache Storage API
      let cache;
      try {
        if ('caches' in window) {
          cache = await window.caches.open(CACHE_NAME);
          const cachedResponse = await cache.match(s3Key);
          if (cachedResponse) {
            const blob = await cachedResponse.blob();
            const objectUrl = URL.createObjectURL(blob);
            memoryCache[s3Key] = objectUrl;
            return objectUrl;
          }
        }
      } catch (cacheError) {
        console.warn('Cache Storage API error:', cacheError);
      }

      // Cache miss: fetch signed URL from our API
      const signResponse = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
        },
      });

      if (!signResponse.ok) {
        throw new Error(`Failed to fetch signed URL: ${signResponse.statusText}`);
      }

      const signedUrl = await signResponse.text();

      // Fetch the actual image data from S3 using the signed URL
      const imgResponse = await fetch(signedUrl);
      if (!imgResponse.ok) {
        throw new Error(`Failed to fetch image from S3: ${imgResponse.statusText}`);
      }

      const blob = await imgResponse.blob();

      // Store in Cache Storage
      if (cache) {
        try {
          const responseToCache = new Response(blob, {
            headers: {
              'Content-Type': blob.type || 'image/png',
              'Cache-Control': 'public, max-age=31536000',
            }
          });
          await cache.put(s3Key, responseToCache);
        } catch (putError) {
          console.warn('Failed to store screenshot in Cache Storage:', putError);
        }
      }

      const objectUrl = URL.createObjectURL(blob);
      memoryCache[s3Key] = objectUrl;
      return objectUrl;

    } catch (error) {
      console.error(`Error in getCachedScreenshotUrl for ${s3Key}:`, error);
      throw error;
    } finally {
      // Clean up in-flight tracker once resolved
      delete inFlightRequests[s3Key];
    }
  };

  // Store the promise in inFlightRequests
  inFlightRequests[s3Key] = fetchAndCache();
  return inFlightRequests[s3Key];
};
